import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { GroupedRowData, GroupHeaderTitle, MyQueueStatus, TableButton, WorkflowRunInitType, WorkflowRunMode, WorkflowType } from '@ca/core/models';
import { DialogService, ExperimentalFeaturesService, UserNotificationService, UserWorkspaceService, WorkflowRunApiService } from '@ca/core/services';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Guid } from 'guid-typescript';
import { EMPTY } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { getGroupedRowData } from '../../../../utils/utils';
import { defaultBatchCorrelationId } from '../../constants/constants';

@Component({
    selector: 'ca-active-workflows-dialog',
    templateUrl: './active-workflows-dialog.component.html',
    styleUrls: ['./active-workflows-dialog.component.scss'],
})
export class ActiveWorkflowsDialogComponent {
    @Input() set activeWorkflows(activeWorkflows: MyQueueStatus[]) {
        const newRows: MyQueueStatus[] = [];
        const groupedWorkflowsValues = Object.values(this.groupWorkflowById(activeWorkflows));
        for (let index = 0; index < groupedWorkflowsValues.length; index++) {
            const workflowsWithSameBatchCorrelationId = groupedWorkflowsValues[index];
            newRows.push(
                ...workflowsWithSameBatchCorrelationId.map(workflow => {
                    // To be able to show non-batch-run results in a group we need to define a unique UID
                    // so that the data table can group them separately
                    if (workflow.batchCorrelationId === defaultBatchCorrelationId) {
                        workflow.batchCorrelationId = Guid.create().toString();
                    }

                    return workflow;
                }),
            );
        }

        const rows = newRows.sort((a, b) => (b.executedAt && a.executedAt ? new Date(b.executedAt).getTime() - new Date(a.executedAt).getTime() : 0));
        this.groupedRows = getGroupedRowData({
            data: rows,
            groupByProperty: 'batchCorrelationId',
            headerTitleFn: (groupKey: string, data: { batchCorrelationId: string; workflowTitle: string }[]): string => {
                const batchRunsInGroup = data.filter(item => item.batchCorrelationId === groupKey);
                const title = batchRunsInGroup[0].workflowTitle; // we always show the first title

                if (batchRunsInGroup.length === 1) {
                    return title;
                }

                return `Batch Run ${title} | ${batchRunsInGroup.length} ${batchRunsInGroup.length > 1 ? 'Workflows' : 'Workflow'}`;
            },
        }) as GroupedRowData<GroupHeaderTitle, MyQueueStatus>[];
    }
    @Input() isAdminUser: boolean = false;

    groupedRows: GroupedRowData<GroupHeaderTitle, MyQueueStatus>[];

    buttons: TableButton[] = [
        new TableButton({
            uitAttribute: 'active-workflow-actions-cancel-workflow',
            icon: 'trash',
            handler: (item: MyQueueStatus) => this.cancelWorkflow(item),
            style: 'btn-secondary',
            label: 'Cancel workflow',
            showIconOnly: true,
        }),
    ];

    constructor(
        private activeModal: NgbActiveModal,
        private dialogService: DialogService,
        private router: Router,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private userWorkspaceService: UserWorkspaceService,
        private workflowRunService: WorkflowRunApiService,
        private experimentalFeaturesService: ExperimentalFeaturesService,
    ) {}

    getRunModeText(isStepByStep: boolean): string {
        const translationKey = isStepByStep
            ? 'WORKFLOWS.RUNNER.RUN_MODES.STEP_BY_STEP.BUTTON_TEXT'
            : 'WORKFLOWS.RUNNER.RUN_MODES.SINGLE_AND_BATCH_RUN.BUTTON_TEXT';
        return this.translateService.instant(translationKey);
    }

    dismiss(): void {
        this.activeModal.close();
    }

    isClickable(queueStatus: MyQueueStatus): boolean {
        return (this.experimentalFeaturesService.isMEDProjectActive ? false : queueStatus.workflowType === WorkflowType.UNDEFINED) || this.isAdminUser;
    }

    jumpIntoRunner(queueStatus: MyQueueStatus): void {
        if (!this.isClickable(queueStatus)) {
            return;
        }

        const { workflowId, isInteractiveWorkflow } = queueStatus;
        this.activeModal.close();
        // this is necessary because this component is being used at multiple places
        // and when it is used in runner we want to force destroying and recreating runner component
        // but this is not happening if we don't change the route first because Angular reuses the component
        this.router.navigateByUrl('/', { skipLocationChange: true }).then(() =>
            this.router.navigate(['workflows', 'runner', workflowId], {
                queryParams: {
                    runInitType: WorkflowRunInitType.ByActiveWorkflow,
                    runMode: isInteractiveWorkflow ? WorkflowRunMode.StepByStep : WorkflowRunMode.SingleAndBatchRun,
                    refreshToken: Guid.create(),
                },
                state: { workflowRun: queueStatus },
            }),
        );
    }

    cancelWorkflow(queueStatus: MyQueueStatus): void {
        const yesText: string = this.translateService.instant('GENERAL.YES');
        const noText: string = this.translateService.instant('GENERAL.NO');
        const confirmMessage: string = this.translateService.instant('WORKFLOWS.RUNNER.DIALOG.ACTIVE_WORKFLOWS.CANCEL_WORKFLOW_DIALOG');

        this.dialogService
            .openConfirm({ message: confirmMessage, confirmText: yesText, cancelText: noText })
            .result.then(() => {
                this.userNotificationService.showSpinner();
                this.workflowRunService
                    .deleteRun(queueStatus.id)
                    .pipe(
                        map(() => {
                            this.userNotificationService.showSuccess('', 'WORKFLOWS.RUNNER.DIALOG.ACTIVE_WORKFLOWS.NOTIFICATIONS.SUCCESS_TITLE');
                            this.userNotificationService.hideSpinner();
                            this.userWorkspaceService.updateMyResults$().subscribe();
                        }),
                        catchError((err: unknown) => {
                            this.userNotificationService.showError('', 'WORKFLOWS.RUNNER.DIALOG.ACTIVE_WORKFLOWS.NOTIFICATIONS.ERROR_TITLE');
                            console.error(err);
                            this.userNotificationService.hideSpinner();
                            return EMPTY;
                        }),
                    )
                    .subscribe();
            })
            .catch(() => {
                // do nothing
            });
    }

    private groupWorkflowById(queues: MyQueueStatus[]): { [id: string]: MyQueueStatus[] } {
        const groupedQueues = {};
        for (const q of queues) {
            if (groupedQueues[q.batchCorrelationId]) {
                const index = groupedQueues[q.batchCorrelationId].findIndex(x => x.id === q.id);
                if (index === -1) {
                    groupedQueues[q.batchCorrelationId].push(q);
                } else {
                    groupedQueues[q.batchCorrelationId][index] = q;
                }
            } else {
                groupedQueues[q.batchCorrelationId] = [q];
            }
        }
        return groupedQueues;
    }
}
